/**
 * Attribution Service
 * Handles performance attribution and contributor analysis API calls.
 * Uses a longer timeout (90s) for heavy attribution endpoints so they don't cancel at 30s.
 */

import httpClient, { extractData } from './http-client';
import {
  ApiResponse,
  PortfolioAttribution,
  ContributorBond,
  Period,
  Metric,
} from '../types';

const ATTRIBUTION_TIMEOUT_MS = 90000; // 90s for heavy attribution queries (default axios is 30s)

class AttributionService {
  /**
   * Get available universe dates for period picker (from backend daily data)
   */
  async getAvailableDates(): Promise<string[]> {
    const response = await httpClient.get<ApiResponse<string[]>>(
      '/bonds/attribution/available-dates',
      { timeout: ATTRIBUTION_TIMEOUT_MS }
    );
    return extractData(response);
  }

  /**
   * Get portfolio-level performance attribution
   */
  async getPortfolioAttribution(
    period: Period = 'MTD',
    startDate?: string,
    endDate?: string
  ): Promise<PortfolioAttribution> {
    const response = await httpClient.get<ApiResponse<PortfolioAttribution>>(
      '/bonds/attribution/portfolio',
      { params: { period, startDate, endDate }, timeout: ATTRIBUTION_TIMEOUT_MS }
    );
    return extractData(response);
  }

  /**
   * Get period performance (CB, Equity, Delta-Neutral)
   */
  async getPeriodPerformance(
    period: Period = 'MTD',
    startDate?: string,
    endDate?: string
  ): Promise<any> {
    const params: Record<string, string> = { period };
    if (startDate != null && startDate !== '') params.startDate = startDate;
    if (endDate != null && endDate !== '') params.endDate = endDate;
    const response = await httpClient.get<ApiResponse<any>>(
      '/bonds/attribution/period-performance',
      { params, timeout: ATTRIBUTION_TIMEOUT_MS }
    );
    return extractData(response);
  }

  /**
   * Get performance breakdown by filter dimension
   */
  async getPerformanceByFilter(
    filterType: 'profile' | 'sector' | 'rating' | 'size',
    period: Period = 'MTD',
    startDate?: string,
    endDate?: string
  ): Promise<any> {
    const params: Record<string, string> = { filterType, period };
    if (startDate != null && startDate !== '') params.startDate = startDate;
    if (endDate != null && endDate !== '') params.endDate = endDate;
    const response = await httpClient.get<ApiResponse<any>>(
      '/bonds/attribution/by-filter',
      { params, timeout: ATTRIBUTION_TIMEOUT_MS }
    );
    return extractData(response);
  }

  /**
   * Get top performing bonds
   */
  async getTopContributors(
    metric: Metric = 'cb',
    period: Period = 'MTD',
    limit: number = 10,
    startDate?: string,
    endDate?: string
  ): Promise<ContributorBond[]> {
    const params: Record<string, string | number> = { metric, period, limit };
    if (startDate != null && startDate !== '') params.startDate = startDate;
    if (endDate != null && endDate !== '') params.endDate = endDate;
    const response = await httpClient.get<ApiResponse<ContributorBond[]>>(
      '/bonds/attribution/top-contributors',
      { params, timeout: ATTRIBUTION_TIMEOUT_MS }
    );
    return extractData(response);
  }

  /**
   * Get worst performing bonds
   */
  async getWorstContributors(
    metric: Metric = 'cb',
    period: Period = 'MTD',
    limit: number = 10,
    startDate?: string,
    endDate?: string
  ): Promise<ContributorBond[]> {
    const params: Record<string, string | number> = { metric, period, limit };
    if (startDate != null && startDate !== '') params.startDate = startDate;
    if (endDate != null && endDate !== '') params.endDate = endDate;
    const response = await httpClient.get<ApiResponse<ContributorBond[]>>(
      '/bonds/attribution/worst-contributors',
      { params, timeout: ATTRIBUTION_TIMEOUT_MS }
    );
    return extractData(response);
  }

  /**
   * Get portfolio performance history time series
   */
  async getPortfolioHistory(
    period: Period = 'MTD',
    startDate?: string,
    endDate?: string
  ): Promise<any> {
    const params: Record<string, string> = { period };
    if (startDate != null && startDate !== '') params.startDate = startDate;
    if (endDate != null && endDate !== '') params.endDate = endDate;
    const response = await httpClient.get<ApiResponse<any>>(
      '/bonds/attribution/history',
      { params, timeout: ATTRIBUTION_TIMEOUT_MS }
    );
    return extractData(response);
  }
}

export const attributionService = new AttributionService();
export default attributionService;
